import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const PHOTOS = [
  { src: "/gallery-xerocon.webp", caption: "Xero Roadshow under Concorde", tilt: -2 },
  { src: "/gallery-xero-clouds.webp", caption: "Xero do awesome accountancy conferences", tilt: 1.5 },
  { src: "/gallery-coast.webp", caption: "One of our favourite things to do — the coastal path", tilt: -1 },
  { src: "/gallery-boat.webp", caption: "Luke at the helm in Italy", tilt: 2 },
  { src: "/gallery-tea-hills.webp", caption: "Tea country in Sri Lanka, far from HMRC", tilt: -1.5 },
  { src: "/gallery-cycle.webp", caption: "We cycled from Calais to Amsterdam (256 miles)", tilt: 1 },
];

export const OwnersGallery = () => {
  const trackRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  useEffect(() => {
    update();
    const el = trackRef.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-gallery-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.7;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const btn =
    "w-11 h-11 rounded-full border border-[#103080]/15 bg-white text-[#103080] flex items-center justify-center transition-[transform,opacity,background-color] duration-300 hover:bg-[#103080] hover:text-white hover:-translate-y-0.5 disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#103080] disabled:hover:translate-y-0";

  return (
    <div data-testid="owners-gallery" className="mt-20 sm:mt-28">
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-3">Off the clock</p>
          <h3 className="font-serif font-semibold tracking-tight text-[#103080] text-2xl sm:text-3xl">
            A few of our favourite snaps.
          </h3>
        </div>
        <div className="hidden sm:flex items-center gap-3">
          <button
            type="button"
            aria-label="Previous photos"
            data-testid="gallery-prev-btn"
            onClick={() => scrollBy(-1)}
            disabled={atStart}
            className={btn}
          >
            <ArrowLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Next photos"
            data-testid="gallery-next-btn"
            onClick={() => scrollBy(1)}
            disabled={atEnd}
            className={btn}
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        data-testid="gallery-track"
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 -mx-5 px-5 sm:-mx-8 sm:px-8 scrollbar-hide"
      >
        {PHOTOS.map((p, i) => (
          <motion.figure
            key={p.src}
            data-gallery-card
            data-testid={`gallery-photo-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ rotate: 0, scale: 1.02 }}
            style={{ rotate: p.tilt }}
            className="snap-start shrink-0 bg-white p-3 pb-4 rounded-xl lift-shadow cursor-grab active:cursor-grabbing"
          >
            <img
              src={p.src}
              alt={p.caption}
              loading="lazy"
              draggable="false"
              className="h-64 sm:h-80 w-auto max-w-[80vw] rounded-md object-cover select-none"
            />
            <figcaption
              className="mt-3 font-serif italic text-sm sm:text-base text-[#103080]/80 leading-snug"
              style={{ width: 0, minWidth: "100%" }}
            >
              {p.caption}
            </figcaption>
          </motion.figure>
        ))}
      </div>

      <p className="sm:hidden text-xs text-[#0F172A]/50 mt-1">Swipe to see more</p>
    </div>
  );
};
